"use client"
import Image from "next/image";
import CheckIcon from "@/icons/status/check-circle.svg";
import Quote from "@/icons/ui/quote.svg";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { QuizCTA } from "~/components/features/quiz";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import Badge from "~/componentsDS/Badge";
import Reveal from "~/componentsDS/Reveal";
import { Tooltip } from "~/components/ui/Tooltip";
import Overline from "~/componentsDS/Typography/Overline";
import useLpCtaText from "~/hooks/useLpCtaText";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import type { GtmEvent } from "~/lib/gtm/getGtmEventData";

const DEFAULT_SUPPORTS = [
    "Clearer thinking and stable moods",
    "Deeper, more restorative sleep",
    "Healthier libido and sexual comfort",
    "Steadier energy and metabolism",
    "Bone and brain protection",
    "Vaginal health — without separate creams"
];

type TreatmentsSectionProps = {
    supports?: string[];
    ctaTitle?: string;
    ctaEventData?: GtmEvent["value"];
    imageAlt?: string;
};

const tooltipContent = (
    <div>
        <Heading as="h3" size="2xs" weight="500" className="mb-1">FDA announcement</Heading>
        <Paragraph>The FDA’s decision reflects updated research confirming that hormone therapy are both safe and beneficial.</Paragraph>
    </div>
);

const eventData = {
    event: "user_interaction",
    value: {
        event_name: GTM_EVENT_NAMES.LP_INTERACTION,
        widget: "lp_fda_announcement",
        meta_data: {
            click_text: "More info"
        },
    },
};

const CTA_TITLE = "See if Oestra® Is Right for You";

const TreatmentsSection = ({
    supports = DEFAULT_SUPPORTS,
    ctaTitle: ctaTitleProp = CTA_TITLE,
    ctaEventData,
    imageAlt = "Body with Oestra cream",
}: TreatmentsSectionProps) => {

    const ctaTitle = useLpCtaText(ctaTitleProp)

    return (
        <section className="relative lg:py-[96px] mb-16 lg:mb-[96px] lg:bg-pureWhite rounded-3xl overflow-hidden">
            <div className="hidden lg:block absolute bottom-0 lg:left-[calc(50%_-_380px)] lg:-translate-x-1/2 2xl:translate-x-0 2xl:left-0 aspect-[1910/1070] w-auto h-[84%] shrink-0 overflow-hidden">
                <Image
                    src="https://res.cloudinary.com/dirscuuhp/images/v1777380059/disrupt-img-new/disrupt-img-new.webp"
                    alt={imageAlt}
                    fill
                    sizes="100vw"
                    quality={95}
                    className="object-cover"
                />
            </div>
            <ElementorContainer innerClassName="relative z-[1]">
                <div className="flex flex-col lg:flex-row justify-between lg:gap-6 w-full">
                    <div className="hidden lg:flex justify-center items-end w-full">
                        <div className="w-[388px] rounded-[16px] border border-pureWhite/50 bg-trueBlack/20 backdrop-blur-[12px] px-4 py-4">
                            <Reveal>
                                <div className="flex gap-4">
                                    <Quote className="w-6 h-6 shrink-0 text-pureWhite" />
                                    <div className="text-pureWhite">
                                        <Paragraph size="lg" className="mb-2">Estrogen is one of the most effective longevity interventions for women.</Paragraph>
                                        <Overline size="sm" className="mb-[2px]"><span className="font-medium">FDA announcement, </span><span className="text-gray100">November 2025</span></Overline>

                                        <Tooltip variant="primary" content={tooltipContent} eventData={eventData}>
                                            <Overline size="sm" weight="500" className="text-secondary300 underline [text-underline-position:from-font]">More info</Overline>
                                        </Tooltip>
                                    </div>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                    <div className="lg:w-[516px] shrink-0 text-primary500">
                        <Badge className="text-primary300 font-medium mb-4">
                            What Oestra<sup>®</sup> treats
                        </Badge>
                        <Reveal>
                            <Heading as="h2" size="sm" sizes={{ lg: "md" }} className="mb-6">
                                Designed for the <Heading as="i" weight="400" font="featureDisplay" className="text-[28px] lg:text-[36px]">symptoms</Heading> that actually disrupt your life
                            </Heading>
                        </Reveal>
                        <Paragraph size="lg" className="mb-4">
                            Oestra<sup>®</sup> supports:
                        </Paragraph>
                        <ul className="flex flex-col gap-2 mb-4">
                            {supports.map((item) => (
                                <li key={item} className="flex gap-2">
                                    <CheckIcon className="w-[22px] h-[22px] shrink-0" />
                                    <Paragraph size="lg" className="pt-[1px]">{item}</Paragraph>
                                </li>
                            ))}
                        </ul>
                        <Paragraph size="lg" className="mb-6">
                            This isn’t about chasing one symptom.
                        </Paragraph>
                        <Paragraph size="lg" weight="500" className="mb-8">
                            It’s about restoring steady hormone levels so your body can function normally again.
                        </Paragraph>
                        <QuizCTA
                            variant="primary"
                            eventData={ctaEventData ?? {
                                event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                                widget: "our_science",
                                meta_data: {
                                    click_text: ctaTitle
                                },
                            }}
                            className="max-md:w-full"
                            href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                            size="md"
                        >
                            <TextWithRegisteredMark text={ctaTitle} />
                        </QuizCTA>
                    </div>
                </div>
            </ElementorContainer>

            <div className="relative lg:hidden aspect-[400/186] w-full mt-6">
                <Image
                    src="https://res.cloudinary.com/dirscuuhp/images/v1775732475/disrupt-img-mob/disrupt-img-mob.webp"
                    alt={imageAlt}
                    fill
                    sizes="100vw"
                    quality={95}
                    className="object-cover"
                />
            </div>
        </section>
    )
};

export default TreatmentsSection;
